import { useId } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { useEntityProfile } from '@/features/profiles/hooks/useEntityProfile';
import type { InvestorProfileData } from '@/features/profiles/api/investor-profile.api';
import { usePermission } from '@/hooks/usePermission';
import { DASHBOARD_STRINGS } from '@/lib/constants/dashboard-strings';
import { PERMISSIONS } from '@/lib/constants/permissions';
import { ROUTES } from '@/lib/constants/routes';
import type { ActiveMembership } from '@/lib/types/auth.types';

const S = DASHBOARD_STRINGS.INVESTOR_PROFILE_CTA;

/** Readiness at which the profile counts for matching (the Backend's own rule). */
const COMPLETE_PCT = 100;

export interface DashboardInvestorProfileCtaProps {
  /** The investor the member is acting for. */
  membership: ActiveMembership;
}

/**
 * The investor's prompt to complete the profile, directly under the welcome banner
 * (MYN-275, FR-185, A-201 to A-203).
 *
 * - A member who can edit the profile (an admin, or a member holding
 *   `entity.profile.edit`) gets a compact card with a button to the Investor Profile
 *   while the STORED readiness is below 100. The card names no percentage. A response
 *   with no readiness counts as 0, so the card shows rather than hides.
 * - A member who cannot edit gets a lighter link to view the profile, with a note to ask
 *   their admin, whether or not the profile is complete (A-202).
 * - Loading or a failed load renders nothing, so nothing flashes in.
 *
 * Reads the profile through `useEntityProfile`, the query the profile page and its save
 * share, so the card is gone as soon as the save that completes the profile lands.
 */
export function DashboardInvestorProfileCta({ membership }: DashboardInvestorProfileCtaProps) {
  const { data, isPending, isError } = useEntityProfile(membership);
  const headingId = useId();
  const canEdit = usePermission(PERMISSIONS.ENTITY_PROFILE_EDIT);
  if (isPending || isError || !data) return null;

  if (!canEdit) {
    return (
      <p className="m-0 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm text-slate">
        <Button asChild variant="link">
          <Link to={ROUTES.INVESTOR_PROFILE}>{S.VIEW_LINK}</Link>
        </Button>
        <span>{S.ASK_ADMIN}</span>
      </p>
    );
  }

  const profile: InvestorProfileData = data;
  const readiness = profile.matchingReadinessPct ?? 0;
  if (readiness >= COMPLETE_PCT) return null;

  return (
    <Card shadow="soft" hoverEffect={false} className="w-full p-5!">
      <section
        aria-labelledby={headingId}
        className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center"
      >
        <div className="flex flex-col gap-1">
          <h2 id={headingId} className="text-h4 text-teal">
            {S.TITLE}
          </h2>
          <p className="m-0 text-sm text-slate">{S.BODY}</p>
        </div>
        <Button asChild variant="green" className="shrink-0 px-5! py-2.5! text-sm">
          <Link to={ROUTES.INVESTOR_PROFILE}>{S.ACTION}</Link>
        </Button>
      </section>
    </Card>
  );
}
